import { Body, IconButton } from '@assembly-js/design-system';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import type { ActivationStepId } from './activationSteps';
import { LightBeam } from './LightBeam';

export interface ActivationChecklistStep {
  id: ActivationStepId;
  label: string;
  isComplete: boolean;
  onClick: () => void;
}

interface ActivationChecklistCardProps {
  title: string;
  steps: ActivationChecklistStep[];
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

// The 24px-tall slot sets the row height; the 16px circle sits centered in it.
const StepMark = ({ isComplete }: { isComplete: boolean }) => (
  <span
    aria-hidden="true"
    className="flex h-6 w-4 shrink-0 items-center justify-center"
  >
    {isComplete ? (
      <span className="flex size-4 items-center justify-center rounded-full bg-black-heading text-white">
        <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
          <path
            d="M1.5 4L3.5 6L6.5 2"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    ) : (
      <span className="box-border block size-4 rounded-full border border-solid border-gray-300" />
    )}
  </span>
);

/**
 * One onboarding-activation checklist in the sidebar: title, step count, one bar
 * segment per step and a row per step. Collapsed, it's a 48px pill with the
 * count, lit by a light beam on hover.
 */
export const ActivationChecklistCard = ({
  title,
  steps,
  isCollapsed,
  onToggleCollapse,
}: ActivationChecklistCardProps) => {
  const { t } = useTranslation();
  const completed = steps.filter((step) => step.isComplete).length;
  const total = steps.length;

  if (isCollapsed) {
    return (
      <button
        type="button"
        aria-expanded={false}
        onClick={onToggleCollapse}
        className="group relative mb-1 box-border flex h-12 w-full cursor-pointer appearance-none items-center gap-2 overflow-hidden rounded-lg border border-solid border-assembly-blue-200 bg-assembly-blue-100 py-0 pl-3 pr-3.5 transition-colors duration-300 hover:border-assembly-blue-400"
      >
        <LightBeam />
        <Body
          tag="span"
          size="sm"
          className="relative min-w-0 flex-1 truncate text-left leading-4.5! text-black-heading"
        >
          {title}
        </Body>
        <Body
          tag="span"
          size="xs"
          className="relative shrink-0 leading-4! tabular-nums text-gray-small"
        >
          {t(($) => $.page_apps.app_builder.getting_started.steps_count, {
            completed,
            total,
          })}
        </Body>
      </button>
    );
  }

  return (
    <div className="mb-1 box-border w-full rounded-lg border border-solid border-gray-150 bg-white px-3 pb-2 pt-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-0.5">
          <Body
            tag="span"
            size="sm"
            className="leading-4.5! text-black-heading"
          >
            {title}
          </Body>
          <Body
            tag="span"
            size="xs"
            className="leading-4! tabular-nums text-gray-small"
          >
            {t(($) => $.page_apps.app_builder.getting_started.steps_completed, {
              completed,
              total,
            })}
          </Body>
        </div>
        {/* The prototype's chevron is 12px in a 20px target, smaller than IconButton's sm. */}
        <IconButton
          icon="ChevronDown"
          variant="minimal"
          size="sm"
          aria-expanded
          aria-label={t(
            ($) => $.page_apps.app_builder.getting_started.collapse_checklist,
          )}
          onClick={onToggleCollapse}
          className="-mr-1 size-5! shrink-0 border-0! p-0! text-light-gray hover:text-black-heading [&_svg]:size-3!"
        />
      </div>
      <div className="mb-2 mt-2.5 flex gap-1" aria-hidden="true">
        {steps.map((step) => (
          <div
            key={step.id}
            className="h-[3px] flex-1 overflow-hidden rounded-full bg-gray-150"
          >
            <div
              className={clsx(
                'h-full rounded-full bg-dark-font transition-[width] duration-500 ease-out',
                step.isComplete ? 'w-full' : 'w-0',
              )}
            />
          </div>
        ))}
      </div>
      <div className="-mx-1.5 flex flex-col">
        {steps.map((step) => (
          <button
            key={step.id}
            type="button"
            disabled={step.isComplete}
            onClick={step.onClick}
            className={clsx(
              'box-border flex w-full appearance-none items-center gap-2 rounded-lg border-0 bg-transparent px-1.5 py-1 text-left',
              step.isComplete
                ? 'cursor-default'
                : 'cursor-pointer transition-colors hover:bg-gray-100',
            )}
          >
            <StepMark isComplete={step.isComplete} />
            <Body
              tag="span"
              size="xs"
              className={clsx(
                'min-w-0 flex-1 truncate leading-4!',
                step.isComplete
                  ? 'text-light-gray line-through'
                  : 'text-black-heading',
              )}
            >
              {step.label}
            </Body>
          </button>
        ))}
      </div>
    </div>
  );
};
